// TODO (07/21/2026): Remove this file when the dialog is no longer needed on 10/01/2026.
import { WorkflowManagementBetaAccessDialog } from './workflow-management-beta-access-dialog.js';
import { checkIsDevelopmentOrigin } from '../../modules/CheckOrigin.js';
import { isFlagEnabled } from '../../modules/FeatureFlags.js';

// The zero based month and day the beta window opens on, currently August 5th 2026.
const BETA_WINDOW_START_MONTH = 7;
const BETA_WINDOW_START_DAY = 5;

// The zero based month and day the beta window closes on, currently September 2nd 2026.
const BETA_WINDOW_END_MONTH = 8;
const BETA_WINDOW_END_DAY = 2;

/**
 * Determine whether the current date is within, or after, the 2026 beta window.
 * @returns {{isWithinBetaWindow: boolean, isAfterBetaWindow: boolean}}
 */
function determineBetaWindowStatus() {
  const now = new Date();
  const betaWindowStart = new Date(2026, BETA_WINDOW_START_MONTH, BETA_WINDOW_START_DAY);
  const betaWindowEnd = new Date(2026, BETA_WINDOW_END_MONTH, BETA_WINDOW_END_DAY);
  const isWithinBetaWindow = now >= betaWindowStart && now < betaWindowEnd;
  const isAfterBetaWindow = now >= betaWindowEnd;
  return { isWithinBetaWindow, isAfterBetaWindow };
}

/**
 * Reads and writes the Workflow Management beta-access state and shows the dialog.
 */
export class WorkflowManagementBetaAccessDialogService {
  /**
   * @param {object} firebaseWorker Provides RTDB access through ref and database.
   * @param {object} firebaseApp Provides the callable functions used to enable early access.
   */
  constructor(firebaseWorker, firebaseApp) {
    this.firebaseWorker = firebaseWorker;
    this.firebaseApp = firebaseApp;
  }

  /**
   * Determine whether the active company has either the data collection or engineering design pricing module.
   * @param {string} userGroup
   * @returns {Promise<boolean>}
   */
  async checkCompanyHasRequiredModules(userGroup) {
    if (!this.firebaseWorker) return false;

    const companyPricingModules =
      (await this.firebaseWorker.ref(`photoheight/company_space/${userGroup}/subscription/pricing_modules`).once('value')).val() ?? {};
    const hasDataCollectionModule = companyPricingModules.data_collection === true;
    const hasEngineeringDesignModule = companyPricingModules.engineering_design === true;
    return hasDataCollectionModule || hasEngineeringDesignModule;
  }

  /**
   * Determine whether the user has been granted the Client Support Dashboard role at their root company.
   * @param {string} rootCompany
   * @param {string} userId
   * @returns {Promise<boolean>}
   */
  async checkUserHasClientSupportDashboardRole(rootCompany, userId) {
    if (!this.firebaseWorker || !rootCompany) return false;

    const supportDashboardAccessPath = `users/${rootCompany}/${userId}/roles/photoheight/support_dashboard_access`;
    const hasSupportDashboardAccess = (await this.firebaseWorker.ref(supportDashboardAccessPath).once('value')).val();
    return hasSupportDashboardAccess === true;
  }

  /**
   * Record that a company admin has dismissed the Workflow Management beta-access dialog against their active company.
   * @param {string} userGroup
   * @param {string} userId
   */
  markWorkflowManagementBetaDialogDismissed(userGroup, userId) {
    if (!this.firebaseWorker) return;

    const hasSeenFlagReference = this.firebaseWorker.ref(
      `photoheight/company_space/${userGroup}/user_data/${userId}/has_seen_workflow_management_beta_dialog`
    );
    hasSeenFlagReference.set(true);
  }

  /**
   * Enable Workflow Management early access for the whole company through the existing cloud function.
   * @param {string} userGroup
   */
  async enableWorkflowManagementEarlyAccessForCompany(userGroup) {
    if (!this.firebaseApp) return;

    const updateFeatureFlagStates = this.firebaseApp.functions().httpsCallable('updateFeatureFlagStates');
    await updateFeatureFlagStates({
      flagName: 'workflow_management_early_access',
      entityType: 'company',
      entityId: userGroup,
      state: true
    });
  }

  /**
   * Determine eligibility and, when eligible, show the Workflow Management beta-access dialog to a company admin during August 2026.
   * @param {boolean} isCompanyAdmin
   * @param {string} userGroup The active company, used to check modules, enable the feature, and store the dismissed flag.
   * @param {string} userId
   * @param {string} rootCompany The company the user belongs to, used to check for the Client Support Dashboard role.
   */
  async showWorkflowManagementBetaAccessDialog(isCompanyAdmin, userGroup, userId, rootCompany) {
    // Do nothing without a database connection, without the identifiers needed to key the flag, or on a development origin
    const isMissingDialogPrerequisites = !this.firebaseWorker || !userGroup || !userId || checkIsDevelopmentOrigin();
    if (isMissingDialogPrerequisites) return;

    // The dialog is built for the desktop app, so never show it to mobile users
    const isMobileUser = globalThis.isMobile === true;
    if (isMobileUser) return;

    // Only company admins are eligible to see the dialog
    if (!isCompanyAdmin) return;

    // Support users work inside customer companies, so don't spam them with the dialog
    const hasClientSupportDashboardRole = await this.checkUserHasClientSupportDashboardRole(rootCompany, userId);
    if (hasClientSupportDashboardRole) return;

    // Resolve the time window and delete the flag if the beta window has passed
    const hasSeenFlagPath = `photoheight/company_space/${userGroup}/user_data/${userId}/has_seen_workflow_management_beta_dialog`;
    const { isWithinBetaWindow, isAfterBetaWindow } = determineBetaWindowStatus();
    if (isAfterBetaWindow) {
      await this.firebaseWorker.ref(hasSeenFlagPath).remove();
      return;
    }
    if (!isWithinBetaWindow) return;

    // Do not show the dialog if this admin has already dismissed it
    const hasSeenFlagReference = this.firebaseWorker.ref(hasSeenFlagPath);
    const hasSeenDialog = (await hasSeenFlagReference.once('value')).val();
    if (hasSeenDialog) return;

    // Require the active company to have one of the qualifying pricing modules
    const hasRequiredModules = await this.checkCompanyHasRequiredModules(userGroup);
    if (!hasRequiredModules) return;

    // Build the dialog properties
    const isEarlyAccessAlreadyEnabled = await isFlagEnabled('workflow_management_early_access', this.firebaseWorker.database(), {
      companyId: userGroup
    });
    const dialogProperties = { userGroup, userId, earlyAccessAlreadyEnabled: isEarlyAccessAlreadyEnabled };

    // Build the dialog and give it this service so it can record dismissal and enable early access
    const dialog = new WorkflowManagementBetaAccessDialog();
    Object.assign(dialog, dialogProperties);
    dialog.service = this;
    document.body.appendChild(dialog);

    // Open the dialog once it has finished rendering
    await dialog.updateComplete;
    dialog.open();
  }
}
